import { StandardProps } from "../componentMap";
import { useVariant } from "../../hooks/useVariant/useVariant";
import { getPriceOrRange } from "@JohnLewisPartnership/jl-design-system/dist/components/price/helpers/getPriceOrRange";
import { useSeo } from "../../hooks/useSeo/useSeo";
import { ProductStatusBoxUI } from "./ProductStatusBoxUI";
import { getColourGroupForVariant } from "../../utils/product/getColourGroupForVariant";
import {
  getPriceValueOrRange,
  handleAccordionDescriptionCTAClick,
  handleCTAClickToScroll,
} from "./utils";
import { ensureIsRetired } from "../../utils/product/ensureIsRetired";
import { useShowStoreStockButton } from "../../hooks/useShowStoreStockButton/useShowStoreStockButton";
import { isFutureRelease } from "../../utils/variant/isFutureRelease";
import { isAvailableToPreOrder } from "../../utils/variant/isAvailableToPreOrder";

const imageGallerySelector = "#image-gallery";

const notifyMeSelector = '[data-testid="notavailable:container"]';
const checkInStoreStockSelector =
  '[data-testid="product:basket:out-of-stock-experience"]';

type ProductStatusBoxProps = Pick<
  StandardProps,
  "product" | "configProps" | "runtimeConfig"
>;

export const ProductStatusBox = ({
  product,
  configProps,
  runtimeConfig: {
    storeStockApiKey,
    stockApiKey,
    storeStockModalApiUrl,
    stockApiBaseUrl,
  },
}: ProductStatusBoxProps) => {
  const { variant, isSelected } = useVariant(product);
  const { heading } = useSeo(product);
  const isRetired = ensureIsRetired(product);
  const showStoreStockButton = useShowStoreStockButton({
    storeStockApiKey,
    hideIfStoreStockUnavailable: true,
    stockApiKey,
    storeStockModalApiUrl,
    stockApiBaseUrl,
    displayCode: variant.displayCode,
    skuId: variant.aliases.skuId,
    isFutureRelease: isFutureRelease(variant),
    isPreOrderable: isAvailableToPreOrder(variant),
  });

  const statusMessage = configProps?.statusMessage;
  const showPrice = configProps?.showPrice === true;

  const { variantPriceRange } = product;

  let priceRange;

  if (showPrice) {
    if (configProps?.useVariantGroups && !isSelected) {
      const variantGroup = getColourGroupForVariant(
        product.variantGroups,
        variant.id,
      );
      const variantGroupPriceRange = variantGroup?.variantPriceRange;

      priceRange = variantGroupPriceRange || variantPriceRange;
    } else {
      priceRange = isSelected ? variant.price : variantPriceRange;
    }
  }

  const formattedPrice = getPriceOrRange(
    getPriceValueOrRange(priceRange?.display),
  );

  const showEmwbis =
    !isRetired &&
    ((isSelected && variant.customerNotifiableEvents?.backInStock) ||
      !isSelected);

  if (!statusMessage) {
    return null;
  }

  return (
    <ProductStatusBoxUI
      image={variant.images.primary}
      statusMessage={statusMessage as string}
      price={formattedPrice}
      productType={product.__typename}
      showEmwbis={showEmwbis}
      showStoreStockButton={showStoreStockButton === "visible"}
      title={heading}
      handleImageClick={handleCTAClickToScroll(imageGallerySelector)}
      handleDescriptionCTA={handleAccordionDescriptionCTAClick}
      handleShowEmwbisClick={handleCTAClickToScroll(notifyMeSelector)}
      handleCheckInStoreStockClick={handleCTAClickToScroll(
        checkInStoreStockSelector,
      )}
    />
  );
};
